import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Typography,
    Paper,
    Grid,
    Avatar,
    Button,
    Chip,
    Stack,
    Divider,
} from '@mui/material';
import {
    Edit as EditIcon,
    Person as PersonIcon,
    ShoppingBag,
    FavoriteBorder,
    LocationOn,
    Home as HomeIcon,
} from '@mui/icons-material';
import { useGetCustomerProfile } from '../../hooks/customer/useGetCustomerProfile';
import { useGetCustomerOrders } from '../../hooks/customer/useGetCustomerOrders';
import { useGetCustomerWishlist } from '../../hooks/customer/useGetCustomerWishlist';
import { useGetCustomerAddresses } from '../../hooks/customer/useGetCustomerAddresses';
import { useAuthContext } from '../../auth/useAuthContext';
import LoadingPage from '../global/LoadingPage';
import WrapperSection from '../global/WrapperSection';
import ProfileEditForm from './ProfileEditForm';
import { useNavigate } from 'react-router-dom';

const ProfileContainer = () => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();

    const { user } = useAuthContext();
    const { profile, isLoading, error, refetch } = useGetCustomerProfile();
    const { orders, meta: ordersMeta } = useGetCustomerOrders();
    const { data: wishlist } = useGetCustomerWishlist();
    const {  meta: addressesMeta } = useGetCustomerAddresses();
    const [isEditMode, setIsEditMode] = useState(false);

    const handleEditSuccess = () => {
        setIsEditMode(false);
        refetch();
    };

    const navigateToAddresses = () => {
        navigate('/addresses');
    };

    const navigateToOrders = () => {
        navigate('/orders');
    };

    const navigateToReturns = () => {
        navigate('/account/returns');
    };

    const navigateToWishlist = () => {
        navigate('/wishlist');
    };

    if (isLoading) return <LoadingPage />;

    if (error) {
        return (
            <Container maxWidth="md">
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="error">
                        {t('profile.loadError')}
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                py: 4,
                px: 2,
                background: isDark
                    ? 'linear-gradient(135deg, #020617, #111827)'
                    : 'linear-gradient(135deg, #EEF2FF, #E0F2FE)',
                minHeight: 'calc(100vh - 64px)',
            }}
        >
            <WrapperSection>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        textAlign={isArabic ? 'right' : 'left'}
                        gutterBottom
                    >
                        {t('profile.title')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign={isArabic ? 'right' : 'left'}
                    >
                        {t('profile.subtitle')}
                    </Typography>
                </Box>

                {/* Quick Actions */}
                <Box sx={{ mb: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={<HomeIcon />}
                        onClick={navigateToAddresses}
                        sx={{
                            minWidth: 140,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {t('profile.addresses')}
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<ShoppingBag />}
                        onClick={navigateToOrders}
                        sx={{
                            minWidth: 140,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {t('profile.orders')}
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<ShoppingBag />}
                        onClick={navigateToReturns}
                        sx={{
                            minWidth: 140,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {t('profile.returns', 'Returns')}
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<FavoriteBorder />}
                        onClick={navigateToWishlist}
                        sx={{
                            minWidth: 140,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {t('profile.wishlist')}
                    </Button>
                </Box>

                {/* Content */}
                <Grid container spacing={3}>
                    {/* Basic Info Card */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 120,
                                    height: 120,
                                    mx: 'auto',
                                    mb: 2,
                                    bgcolor: theme.palette.primary.main,
                                    fontSize: '3rem',
                                }}
                            >
                                {profile?.first_name?.charAt(0)?.toUpperCase() ||
                                    user?.name?.charAt(0)?.toUpperCase() ||
                                    <PersonIcon fontSize="large" />}
                            </Avatar>

                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {profile?.full_name || user?.name || '-'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {user?.email || '-'}
                            </Typography>

                            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                <Chip
                                    label={user?.user_type === 'admin' ? t('profile.admin') : t('profile.customer')}
                                    color="primary"
                                    size="small"
                                />
                                {user?.is_active && (
                                    <Chip
                                        label={t('profile.active')}
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Stack>

                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                fullWidth
                                sx={{ mt: 3 }}
                                onClick={() => setIsEditMode(!isEditMode)}
                            >
                                {isEditMode
                                    ? t('profile.cancelEdit')
                                    : t('profile.editProfile')}
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Quick Stats */}
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                                    }}
                                >
                                    <ShoppingBag sx={{ fontSize: 30, color: 'primary.main', mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold">
                                        {ordersMeta?.total ?? orders?.length ?? 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('profile.orders')}
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                                    }}
                                >
                                    <FavoriteBorder sx={{ fontSize: 30, color: 'error.main', mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold">
                                        {wishlist?.data?.item_count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('profile.wishlist')}
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                                    }}
                                >
                                    <LocationOn sx={{ fontSize: 30, color: 'success.main', mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold">
                                        {addressesMeta?.total || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('profile.addresses')}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Details / Edit Form */}
                        {isEditMode ? (
                            <ProfileEditForm
                                currentData={profile}
                                onSuccess={handleEditSuccess}
                                onCancel={() => setIsEditMode(false)}
                            />
                        ) : (
                            <Paper
                                sx={{
                                    p: 3,
                                    bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                                }}
                            >
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {t('profile.personalInfo')}
                                </Typography>
                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <ProfileField
                                        label={t('profile.firstName')}
                                        value={profile?.first_name}
                                    />
                                    <ProfileField
                                        label={t('profile.lastName')}
                                        value={profile?.last_name}
                                    />
                                    <ProfileField
                                        label={t('profile.gender')}
                                        value={
                                            profile?.gender
                                                ? (profile.gender === 'male' ? t('profile.male') : t('profile.female'))
                                                : '-'
                                        }
                                    />
                                    <ProfileField
                                        label={t('profile.birthdate')}
                                        value={
                                            profile?.birthdate
                                                ? new Date(profile.birthdate).toLocaleDateString(
                                                    i18n.language,
                                                    { year: 'numeric', month: 'long', day: 'numeric' }
                                                )
                                                : '-'
                                        }
                                    />
                                    <ProfileField
                                        label={t('auth.phone')}
                                        value={user?.phone}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() => navigate('/profile/update-phone')}
                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                            >
                                                {t('profile.update')}
                                            </Button>
                                        }
                                    />
                                    <ProfileField
                                        label={t('auth.email')}
                                        value={user?.email}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() => navigate('/profile/update-email')}
                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                            >
                                                {t('profile.update')}
                                            </Button>
                                        }
                                    />
                                    <ProfileField
                                        label={t('profile.password', 'Password')}
                                        value="••••••••"
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() => navigate('/account/security')}
                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                            >
                                                {t('profile.change', 'Change')}
                                            </Button>
                                        }
                                    />
                                    <ProfileField
                                        label={t('profile.notifications', 'Notifications')}
                                        value={t('profile.notificationsValue', 'Email / SMS / Push')}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() => navigate('/account/notifications')}
                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                            >
                                                {t('profile.manage', 'Manage')}
                                            </Button>
                                        }
                                    />
                                    <ProfileField
                                        label={t('profile.preferredLanguage')}
                                        value={user?.lang === 'ar' ? t('languages.arabic') : t('languages.english')}
                                    />
                                    <ProfileField
                                        label={t('profile.createdAt')}
                                        value={
                                            profile?.created_at
                                                ? new Date(profile.created_at).toLocaleDateString(
                                                    i18n.language,
                                                    { year: 'numeric', month: 'short', day: 'numeric' }
                                                )
                                                : '-'
                                        }
                                    />
                                </Grid>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </WrapperSection>
        </Box>
    );
};

const ProfileField = ({ label, value, action }) => (
    <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                    {value || '-'}
                </Typography>
            </Box>
            {action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
        </Box>
    </Grid>
);

export default ProfileContainer;
